import { useState, useEffect, useMemo } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Combobox } from '@/components/ui/Combobox'
import { usePresalesDashboardStore } from '../store'
import { TASK_TEMPLATES } from '../data/taskTemplates'
import { getDefaultDueDate, getTodayForInput } from '../utils/dateHelpers'
import {
  inputClasses,
  useTeamMembers,
  useKeyPersonOptions,
  useTeamMemberOptions,
  useTaskTemplateOptions,
} from '../utils/taskHelpers'
import type { PresalesDeal, PresalesTaskTemplate } from '@/api/types'
import type { TaskCreatePayload } from '../utils/taskHelpers'

interface TaskCreateFormProps {
  /** If provided, deal is preset and locked (deal view). If omitted, user must select a deal. */
  deal?: PresalesDeal
  /** Pre-fill the assigned_to field */
  defaultAssignedTo?: string
  /** Auto-select a deal by ID when the form opens (used by PersonTab) */
  defaultDealId?: number
  /** Called when the user submits the form */
  onSubmit: (dealId: number | null, data: TaskCreatePayload & { prospect_company?: string }) => Promise<void>
  /** Called when the user cancels */
  onCancel: () => void
  /** Compact single-row layout for inline table quick-add */
  compact?: boolean
}

export function TaskCreateForm({
  deal,
  defaultAssignedTo = '',
  defaultDealId,
  onSubmit,
  onCancel,
  compact = false,
}: TaskCreateFormProps) {
  const { deals } = usePresalesDashboardStore()

  // Form state
  const [selectedDealId, setSelectedDealId] = useState<number | ''>(deal?.id ?? defaultDealId ?? '')
  const [prospectMode, setProspectMode] = useState(false)
  const [prospectCompany, setProspectCompany] = useState('')
  const [template, setTemplate] = useState<PresalesTaskTemplate>('other')
  const [description, setDescription] = useState('')
  const [statusUpdate, setStatusUpdate] = useState('')
  const [assignedTo, setAssignedTo] = useState(defaultAssignedTo)
  const [dueDate, setDueDate] = useState(getDefaultDueDate())
  const [jiraTicket, setJiraTicket] = useState('')
  const [keyPerson, setKeyPerson] = useState('')
  const [isHardDeadline, setIsHardDeadline] = useState(false)
  const [isCriticalPath, setIsCriticalPath] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Shared hooks
  const teamMembers = useTeamMembers()
  const keyPersonOptions = useKeyPersonOptions(teamMembers)
  const teamMemberOptions = useTeamMemberOptions(teamMembers)
  const taskTemplateOptions = useTaskTemplateOptions()

  // Deal options (only needed when no deal prop)
  const openDeals = useMemo(() => deals.filter((d) => !d.is_closed), [deals])
  const dealOptions = useMemo(
    () =>
      openDeals.map((d) => ({
        value: d.id.toString(),
        label: d.deal_name + (d.company_name ? ` (${d.company_name})` : ''),
      })),
    [openDeals],
  )

  // Sync assignedTo if defaultAssignedTo changes (e.g. fullPersonName resolves)
  useEffect(() => {
    if (defaultAssignedTo) {
      setAssignedTo(defaultAssignedTo)
    }
  }, [defaultAssignedTo])

  const handleTemplateChange = (value: string) => {
    const tmpl = TASK_TEMPLATES.find((t) => t.value === value)
    setTemplate(value as PresalesTaskTemplate)
    if (tmpl && tmpl.defaultDescription) {
      setDescription(tmpl.defaultDescription)
    } else {
      setDescription('')
    }
  }

  const handleDealChange = (dealId: number | '') => {
    setSelectedDealId(dealId)
    if (dealId) {
      const selectedDeal = openDeals.find((d) => d.id === dealId)
      if (selectedDeal?.presales_engineer) {
        setAssignedTo(selectedDeal.presales_engineer)
      }
    }
  }

  const handleEnterProspectMode = () => {
    setProspectMode(true)
    setSelectedDealId('')
  }

  const handleBackToDealMode = () => {
    setProspectMode(false)
    setProspectCompany('')
  }

  const resetForm = () => {
    if (!deal) setSelectedDealId('')
    setProspectMode(false)
    setProspectCompany('')
    setTemplate('other')
    setDescription('')
    setStatusUpdate('')
    setAssignedTo(defaultAssignedTo)
    setDueDate(getDefaultDueDate())
    setJiraTicket('')
    setKeyPerson('')
    setIsHardDeadline(false)
    setIsCriticalPath(false)
  }

  const handleSubmit = async () => {
    if (!description.trim()) return

    const dealId = deal?.id ?? (selectedDealId || null) as number | null

    setIsSaving(true)
    try {
      await onSubmit(dealId, {
        template,
        description: description.trim(),
        assigned_to: assignedTo || undefined,
        due_date: dueDate || undefined,
        jira_ticket: jiraTicket.trim() || undefined,
        key_person: keyPerson.trim() || undefined,
        status_update: statusUpdate.trim() || undefined,
        prospect_company: prospectMode && prospectCompany.trim() ? prospectCompany.trim() : undefined,
        is_hard_deadline: isHardDeadline || undefined,
        is_critical_path: isCriticalPath || undefined,
      })
      resetForm()
    } finally {
      setIsSaving(false)
    }
  }

  const canSubmit = !!description.trim()

  // ==================== Compact Layout (inline table quick-add) ====================
  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-[180px] flex-shrink-0">
            <Combobox
              options={taskTemplateOptions}
              value={template}
              onChange={(val) => handleTemplateChange(val)}
              placeholder="Task type..."
            />
          </div>
          <div className="flex-1">
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Task description..."
              className={inputClasses}
            />
          </div>
          <div className="w-[180px] flex-shrink-0">
            <div className="flex gap-1">
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={inputClasses}
              />
              <button
                type="button"
                onClick={() => setDueDate(getTodayForInput())}
                className="px-1.5 py-1 text-[10px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] whitespace-nowrap flex-shrink-0"
              >
                Today
              </button>
            </div>
          </div>
          <div className="w-[140px] flex-shrink-0">
            <Combobox
              options={teamMemberOptions}
              value={assignedTo}
              onChange={(val) => setAssignedTo(val)}
              placeholder="Assigned to..."
              allowFreeText
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={!canSubmit}
          >
            Add
          </Button>
          <button
            onClick={onCancel}
            className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <input
          type="text"
          value={statusUpdate}
          onChange={(e) => setStatusUpdate(e.target.value)}
          placeholder="Status update (optional)..."
          className={inputClasses}
        />
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <input
              type="text"
              value={jiraTicket}
              onChange={(e) => setJiraTicket(e.target.value)}
              placeholder="Jira ticket (optional)..."
              className={inputClasses}
            />
          </div>
          <div className="flex-1">
            <Combobox
              options={keyPersonOptions}
              value={keyPerson}
              onChange={(val) => setKeyPerson(val)}
              placeholder="Key person (optional)..."
              allowFreeText
            />
          </div>
        </div>
      </div>
    )
  }

  // ==================== Standard Layout ====================
  return (
    <div className="mb-4 p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] space-y-3">
      {/* Row 1: Deal (if no deal prop) + Template */}
      {!deal ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Deal</label>
            {prospectMode ? (
              <>
                <input
                  type="text"
                  value={prospectCompany}
                  onChange={(e) => setProspectCompany(e.target.value)}
                  placeholder="Company name..."
                  className={inputClasses}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleBackToDealMode}
                  className="mt-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                >
                  ← Back to deal selection
                </button>
              </>
            ) : (
              <>
                <Combobox
                  options={dealOptions}
                  value={selectedDealId ? selectedDealId.toString() : ''}
                  onChange={(val) => handleDealChange(val ? parseInt(val) : '')}
                  placeholder="Search deals..."
                />
                <button
                  type="button"
                  onClick={handleEnterProspectMode}
                  className="mt-1 text-[10px] text-amber-400 hover:text-amber-300 transition-colors"
                >
                  No deal yet — tag as early engagement
                </button>
              </>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Task Type</label>
            <Combobox
              options={taskTemplateOptions}
              value={template}
              onChange={(val) => handleTemplateChange(val)}
              placeholder="Search task type..."
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Task Type</label>
            <Combobox
              options={taskTemplateOptions}
              value={template}
              onChange={(val) => handleTemplateChange(val)}
              placeholder="Search task type..."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the task..."
              rows={2}
              className={`${inputClasses} resize-none`}
            />
          </div>
        </div>
      )}

      {/* Description (only shown separately when deal picker is visible — to match PersonTab layout) */}
      {!deal && (
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the task..."
            rows={2}
            className={`${inputClasses} resize-none`}
          />
        </div>
      )}

      {/* Status Update */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Status Update</label>
        <input
          type="text"
          value={statusUpdate}
          onChange={(e) => setStatusUpdate(e.target.value)}
          placeholder="e.g., Waiting on client response..."
          className={inputClasses}
        />
      </div>

      {/* Assigned To + Due Date */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[150px]">
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Assigned To</label>
          <Combobox
            options={teamMemberOptions}
            value={assignedTo}
            onChange={(val) => setAssignedTo(val)}
            placeholder="Search team member..."
            allowFreeText
          />
        </div>
        <div className="w-[200px]">
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Due Date</label>
          <div className="flex gap-1">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={inputClasses}
            />
            <button
              type="button"
              onClick={() => setDueDate(getTodayForInput())}
              className="px-2 py-1 text-xs bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] whitespace-nowrap flex-shrink-0"
              title="Set to today"
            >
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Jira Ticket + Key Person */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Jira Ticket</label>
          <input
            type="text"
            value={jiraTicket}
            onChange={(e) => setJiraTicket(e.target.value)}
            placeholder="PROJ-123 or full URL..."
            className={inputClasses}
          />
        </div>
        <div className="flex-1 min-w-[150px]">
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Key Person</label>
          <Combobox
            options={keyPersonOptions}
            value={keyPerson}
            onChange={(val) => setKeyPerson(val)}
            placeholder="Search key person..."
            allowFreeText
          />
        </div>
      </div>

      {/* Task flags + Add button */}
      <div className="flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer">
          <input
            type="checkbox"
            checked={isHardDeadline}
            onChange={(e) => setIsHardDeadline(e.target.checked)}
            className="rounded border-[var(--border-color)] bg-[var(--bg-primary)]"
          />
          <span>🚨 Hard Deadline</span>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer">
          <input
            type="checkbox"
            checked={isCriticalPath}
            onChange={(e) => setIsCriticalPath(e.target.checked)}
            className="rounded border-[var(--border-color)] bg-[var(--bg-primary)]"
          />
          <span>🔴 Critical Path</span>
        </label>
        <div className="ml-auto">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={!canSubmit}
          >
            {prospectMode ? 'Add (Early Deal)' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  )
}
